import {
  DEPARTMENTS_BY_CODE,
  NAME_EDITION,
  NAME_FIRST_YEAR,
  NAME_LAST_YEAR,
  NAME_MAP_FROM,
  isNameIndexable,
  namePath,
  type NameStats,
  type NameSummary,
} from "@listou/shared/schemas/prenoms";
import { Link, useNavigate } from "@tanstack/react-router";
import { ArrowRight, CalendarSearch, X } from "lucide-react";
import { useState } from "react";

import { nameCards } from "../../lib/api";
import { BarRow, FranceMap, Legend, ShareChart, TimeChart } from "./charts";
import {
  BOYS,
  GIRLS,
  GRID,
  departmentName,
  genderOf,
  genderWord,
  medianYear,
  number,
  ordinal,
  percent,
  sum,
} from "./format";
import {
  Breadcrumbs,
  card,
  DepartmentLink,
  Faq,
  NameLink,
  NamesLayout,
  pill,
  PrenomsNav,
  Source,
  YearDepartmentLink,
  YearLink,
} from "./layout";
import { ListouAside } from "./listou-aside";
import { ShareButton } from "./share";
import { breadcrumbJsonLd, datasetJsonLd, faqJsonLd, graph } from "./structured-data";

const indexOfYear = (year: number) => year - NAME_FIRST_YEAR;
const LAST = indexOfYear(NAME_LAST_YEAR);

/** Everything the page says about a name, computed from its yearly series. */
const profile = (data: NameStats) => {
  const peak = Math.max(...data.years);
  const peakYear = NAME_FIRST_YEAR + data.years.indexOf(peak);
  const ranked = data.ranks
    .map((rank, index) => ({ rank, year: NAME_FIRST_YEAR + index }))
    .filter((entry) => entry.rank > 0);
  const best = ranked.toSorted((a, b) => a.rank - b.rank || b.year - a.year)[0];
  const recent = sum(data.years.slice(-5));
  const previous = sum(data.years.slice(-10, -5));
  return {
    peak,
    peakYear,
    best,
    lastRanked: ranked.at(-1),
    latestCount: data.years[LAST] ?? 0,
    latestRank: data.ranks[LAST] ?? 0,
    median: medianYear(data.years),
    change: previous > 0 ? ((recent - previous) / previous) * 100 : null,
    gender: genderOf(data),
    girlsYears: data.years.map((count, index) => count - (data.boysYears[index] ?? 0)),
  };
};

const topDepartments = (data: NameStats, by: "rate" | "count" = "rate") =>
  data.departments.toSorted((a, b) => b[by] - a[by]);
const rankedSex = (data: NameStats) => (genderOf(data) === "boys" ? "masculin" : "féminin");

const faqFor = (data: NameStats, overallRank: number) => {
  const info = profile(data);
  const top = topDepartments(data)[0];
  return [
    {
      question: `Combien de personnes s’appellent ${data.name} en France ?`,
      answer: `Le fichier de l’Insee recense ${number(data.total)} naissances avec le prénom ${data.name} entre ${NAME_FIRST_YEAR} et ${NAME_LAST_YEAR}. Ce nombre compte toutes les naissances, y compris celles des personnes qui ne sont plus en vie.`,
    },
    {
      question: `Quand le prénom ${data.name} a-t-il été le plus donné ?`,
      answer: `Le pic date de ${info.peakYear}, avec ${number(info.peak)} naissances. La moitié des naissances recensées sont antérieures à ${info.median}.`,
    },
    {
      question: `${data.name} est-il un prénom féminin ou masculin ?`,
      answer: `C’est un prénom ${genderWord(info.gender)} : ${percent((data.girls / data.total) * 100)} des ${number(data.total)} naissances sont des filles et ${percent((data.boys / data.total) * 100)} des garçons.`,
    },
    {
      question: `${data.name} est-il un prénom à la mode aujourd’hui ?`,
      answer:
        info.latestRank > 0
          ? `En ${NAME_LAST_YEAR}, ${data.name} est le ${ordinal(info.latestRank)} prénom ${rankedSex(data)} le plus donné, avec ${number(info.latestCount)} naissances.`
          : `En ${NAME_LAST_YEAR}, ${data.name} ne figure pas dans le classement publié par l’Insee${info.latestCount > 0 ? `, pour ${number(info.latestCount)} naissances` : ""}.`,
    },
    ...(top
      ? [
          {
            question: `Où ${data.name} est-il le plus donné ?`,
            answer: `Depuis ${NAME_MAP_FROM}, c’est en ${departmentName(top.code)} que ${data.name} est proportionnellement le plus fréquent : ${number(Math.round(top.rate))} pour 10 000 naissances publiées. Depuis ${NAME_FIRST_YEAR}, c’est le ${ordinal(overallRank)} prénom ${rankedSex(data)} le plus donné.`,
          },
        ]
      : []),
  ];
};

export const nameHead = (data: NameStats, overallRank: number) => {
  const info = profile(data);
  return {
    title: `Prénom ${data.name} : popularité, évolution et carte de France · Listou`,
    image: nameCards.name(data.slug, "og"),
    noindex: !isNameIndexable(data),
    description: `${data.name} : ${number(data.total)} naissances en France depuis ${NAME_FIRST_YEAR}, pic en ${info.peakYear}${
      info.latestRank > 0 ? `, ${ordinal(info.latestRank)} en ${NAME_LAST_YEAR}` : ""
    }. Évolution, classement, filles et garçons, carte par département (Insee).`,
    jsonLd: graph(
      breadcrumbJsonLd(
        [{ label: "Prénoms", to: "/prenoms" }, { label: data.name }],
        namePath.name(data.slug),
      ),
      datasetJsonLd({
        name: `Naissances portant le prénom ${data.name}, ${NAME_FIRST_YEAR}–${NAME_LAST_YEAR}`,
        description: `Statistiques du prénom ${data.name} issues du fichier des prénoms Insee ${NAME_EDITION}.`,
        coverage: `${NAME_FIRST_YEAR}/${NAME_LAST_YEAR}`,
      }),
      faqJsonLd(faqFor(data, overallRank)),
    ),
  };
};

function Sparkline({ values }: { values: number[] }) {
  const top = Math.max(1, ...values);
  const points = values.map(
    (value, index) => `${(index / (values.length - 1)) * 600},${70 - (value / top) * 66}`,
  );
  return (
    <svg
      viewBox="0 0 600 74"
      className="block w-full"
      aria-hidden="true"
      preserveAspectRatio="none"
    >
      <polygon points={`0,74 ${points.join(" ")} 600,74`} fill="#fff" fillOpacity="0.18" />
      <polyline
        points={points.join(" ")}
        fill="none"
        stroke="#fff"
        strokeWidth="2.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Fact({ value, label }: { value: string; label: string }) {
  return (
    <div>
      <dd className="font-display text-3xl font-bold sm:text-4xl">{value}</dd>
      <dt className="mt-1 text-sm text-white/80">{label}</dt>
    </div>
  );
}

function BirthYearForm({ name }: { name: string }) {
  const [year, setYear] = useState("");
  const navigate = useNavigate({ from: "/prenoms/$slug" });
  const value = Number(year);
  const valid = Number.isInteger(value) && value >= NAME_FIRST_YEAR && value <= NAME_LAST_YEAR;
  return (
    <form
      className="flex flex-wrap items-center gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (valid) void navigate({ search: { annee: value } });
      }}
    >
      <label className="flex items-center gap-3">
        <span>Un(e) {name} né(e) en</span>
        <input
          type="number"
          inputMode="numeric"
          min={NAME_FIRST_YEAR}
          max={NAME_LAST_YEAR}
          value={year}
          onChange={(event) => setYear(event.target.value)}
          placeholder="1992"
          className="w-28 rounded-xl border-2 border-line bg-white px-3 py-2"
        />
      </label>
      <button type="submit" className="btn-secondary gap-2" disabled={!valid}>
        <CalendarSearch size={18} aria-hidden="true" />
        Voir la place de mon prénom
      </button>
    </form>
  );
}

/** What the name looked like the year a visitor was born. */
function Personal({ data, year }: { data: NameStats; year: number }) {
  const index = indexOfYear(year);
  const count = data.years[index] ?? 0;
  const rank = data.ranks[index] ?? 0;
  const before = sum(data.years.slice(0, index));
  const after = sum(data.years.slice(index + 1));
  const info = profile(data);
  return (
    <section className={`${card} space-y-4 border-2 border-ink`} aria-labelledby="personal">
      <h2 id="personal" className="text-3xl font-bold">
        {data.name}, né(e) en {year}
      </h2>
      <p className="text-lg">
        {count > 0
          ? `En ${year}, ${number(count)} enfants ont reçu le prénom ${data.name}${rank > 0 ? `, ce qui en faisait le ${ordinal(rank)} prénom ${rankedSex(data)} de l’année` : ""}.`
          : `Le fichier de l’Insee ne publie aucune naissance de ${data.name} en ${year} : le prénom était trop rare cette année-là.`}{" "}
        {`Parmi toutes les naissances recensées, ${percent((before / data.total) * 100)} sont antérieures à ${year} et ${percent((after / data.total) * 100)} postérieures : ${
          year < info.median
            ? `vous faites partie des premiers ${data.name}.`
            : year > info.median
              ? `vous faites partie des ${data.name} les plus récents.`
              : `vous êtes pile dans la génération médiane.`
        }`}
      </p>
      <p className="flex flex-wrap gap-2">
        <YearLink year={year} className={pill}>
          Le top des prénoms {year}
          <ArrowRight size={16} aria-hidden="true" />
        </YearLink>
        <Link to="/prenoms/$slug" params={{ slug: data.slug }} search={{}} className={pill}>
          <X size={16} aria-hidden="true" />
          Effacer l’année
        </Link>
      </p>
    </section>
  );
}

const toggle = (active: boolean) =>
  `rounded-full border px-4 py-2 text-sm font-bold ${active ? "border-ink bg-ink text-white" : "border-line"}`;

function Evolution({ data }: { data: NameStats }) {
  const info = profile(data);
  const [mode, setMode] = useState<"births" | "rank">("births");
  const hasRank = data.ranks.some((rank) => rank > 0);
  const color = info.gender === "boys" ? BOYS : GIRLS;
  return (
    <section className={`${card} space-y-5`}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-3xl font-bold">La vie de {data.name}, année après année</h2>
          <p className="mt-2 max-w-2xl text-muted">
            Survolez la courbe, ou utilisez les flèches du clavier, pour lire chaque année.
          </p>
        </div>
        {hasRank && (
          <div className="flex gap-2" role="group" aria-label="Mesure">
            {(
              [
                ["births", "Naissances"],
                ["rank", "Classement"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                aria-pressed={mode === value}
                onClick={() => setMode(value)}
                className={toggle(mode === value)}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>
      {mode === "births" ? (
        <TimeChart
          label={`Naissances de ${data.name} par année, de ${NAME_FIRST_YEAR} à ${NAME_LAST_YEAR}`}
          series={[{ label: data.name, color, values: data.years }]}
          area
          unit="naissances"
          marker={{ year: info.peakYear, text: `Pic ${info.peakYear} : ${number(info.peak)}` }}
        />
      ) : (
        <>
          <TimeChart
            label={`Classement de ${data.name} par année`}
            series={[{ label: "Rang", color, values: data.ranks }]}
            reverse
          />
          <p className="text-sm text-muted">
            Rang parmi les prénoms {info.gender === "boys" ? "masculins" : "féminins"} de l’année.
            La courbe s’interrompt les années où le prénom n’est pas classé.
          </p>
        </>
      )}
      <details>
        <summary className="cursor-pointer text-sm underline">Voir les chiffres annuels</summary>
        <div className="mt-4 max-h-72 overflow-auto">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 bg-surface">
              <tr>
                <th scope="col">Année</th>
                <th scope="col">Naissances</th>
                <th scope="col">Classement</th>
              </tr>
            </thead>
            <tbody>
              {data.years.map((count, index) => (
                <tr key={index} className="border-t border-line">
                  <th scope="row" className="py-1">
                    <YearLink year={NAME_FIRST_YEAR + index} className="underline" />
                  </th>
                  <td>{number(count)}</td>
                  <td>{data.ranks[index] ? ordinal(data.ranks[index]) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>
    </section>
  );
}

function Generations({ data }: { data: NameStats }) {
  const info = profile(data);
  const decades = Array.from({ length: 13 }, (_, i) => ({
    year: NAME_FIRST_YEAR + i * 10,
    count: sum(data.years.slice(i * 10, i * 10 + 10)),
  }));
  const max = Math.max(1, ...decades.map((d) => d.count));
  const color = info.gender === "boys" ? BOYS : GIRLS;
  const strongest = decades.toSorted((a, b) => b.count - a.count)[0];
  return (
    <section className={`${card} space-y-5`}>
      <h2 className="text-3xl font-bold">Les générations de {data.name}</h2>
      <p className="text-muted">
        La moitié des {data.name} recensés sont nés avant {info.median}. La décennie la plus marquée
        est celle des années {strongest?.year}, avec {number(strongest?.count ?? 0)} naissances.
      </p>
      <div
        className="flex h-52 items-end gap-1.5"
        role="img"
        aria-label={`Naissances de ${data.name} par décennie`}
      >
        {decades.map((decade) => (
          <div
            key={decade.year}
            className="flex h-full min-w-0 flex-1 flex-col justify-end text-center"
          >
            <div
              className="rounded-t-md"
              style={{
                height: `${(decade.count / max) * 88}%`,
                minHeight: decade.count ? 2 : 0,
                background: color,
                opacity: decade.year === strongest?.year ? 1 : 0.55,
              }}
              title={`${decade.year}–${Math.min(decade.year + 9, NAME_LAST_YEAR)} : ${number(decade.count)} naissances`}
            />
            <span className="mt-2 text-[10px] sm:text-xs">{String(decade.year).slice(-2)}</span>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted">
        Décennies : 00 = 1900–1909. La dernière barre couvre seulement 2020–{NAME_LAST_YEAR}.
      </p>
    </section>
  );
}

function SexSplit({ data }: { data: NameStats }) {
  const info = profile(data);
  const girls = (data.girls / data.total) * 100;
  return (
    <section className={`${card} space-y-5`}>
      <h2 className="text-3xl font-bold">Un prénom {genderWord(info.gender)}</h2>
      <p className="text-muted">
        {number(data.girls)} filles ({percent(girls)}) et {number(data.boys)} garçons (
        {percent(100 - girls)}) ont reçu ce prénom depuis {NAME_FIRST_YEAR}.
        {info.gender === "mixed" ? " La répartition a pu beaucoup changer selon les époques :" : ""}
      </p>
      <div
        className="flex h-4 overflow-hidden rounded-full"
        role="img"
        aria-label={`${percent(girls)} de filles`}
      >
        <div style={{ width: `${girls}%`, background: GIRLS }} />
        <div style={{ flex: 1, background: BOYS }} />
      </div>
      <Legend
        items={[
          { label: "Filles", color: GIRLS },
          { label: "Garçons", color: BOYS },
        ]}
      />
      {info.gender === "mixed" && (
        <>
          <ShareChart
            girls={info.girlsYears}
            boys={data.boysYears}
            label={`Part de filles parmi les ${data.name}, année par année`}
          />
          <p className="text-xs text-muted">Part de filles parmi les naissances de chaque année.</p>
        </>
      )}
    </section>
  );
}

function Geography({ data }: { data: NameStats }) {
  const [mode, setMode] = useState<"rate" | "count">("rate");
  const rows = new Map(data.departments.map((row) => [row.code, row]));
  const sorted = topDepartments(data, mode);
  const maximum = Math.max(1, ...sorted.map((row) => row[mode]));
  const color = genderOf(data) === "boys" ? BOYS : GIRLS;
  const unit = mode === "rate" ? " pour 10 000" : " naissances";
  return (
    <section className={`${card} space-y-6`}>
      <div>
        <h2 className="text-3xl font-bold">Où naissent les {data.name} ?</h2>
        <p className="mt-3 text-muted">
          Naissances de {NAME_MAP_FROM} à {NAME_LAST_YEAR}. La fréquence compare le prénom à
          l’ensemble des naissances publiées dans chaque département, filles et garçons réunis.
        </p>
      </div>
      <div className="flex flex-wrap gap-2" role="group" aria-label="Mesure">
        {(
          [
            ["rate", "Pour 10 000 naissances"],
            ["count", "Nombre de naissances"],
          ] as const
        ).map(([value, label]) => (
          <button
            type="button"
            key={value}
            aria-pressed={mode === value}
            onClick={() => setMode(value)}
            className={toggle(mode === value)}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="grid items-center gap-8 md:grid-cols-2">
        <div>
          <FranceMap
            label={`Répartition départementale de ${data.name}, France métropolitaine`}
            fill={(code) => {
              const row = rows.get(code);
              if (!row) return GRID;
              const ratio = Math.sqrt(row[mode] / maximum);
              return `color-mix(in oklab, ${color} ${Math.round(12 + ratio * 88)}%, white)`;
            }}
            describe={(code) => {
              const row = rows.get(code);
              return `${departmentName(code)} : ${row ? `${number(Math.round(row[mode]))}${unit}` : "aucun effectif publié"}`;
            }}
            href={(code) => ({
              to: namePath.department(DEPARTMENTS_BY_CODE.get(code)?.slug ?? ""),
            })}
          />
          <p className="mt-2 text-center text-xs text-muted">
            Du plus faible au plus fort. Gris : aucun effectif publié. Cliquez pour voir les prénoms
            du département.
          </p>
        </div>
        <ol>
          {sorted.slice(0, 8).map((row, index) => {
            const department = DEPARTMENTS_BY_CODE.get(row.code);
            return (
              <BarRow
                key={row.code}
                rank={index + 1}
                name={
                  department ? (
                    <DepartmentLink slug={department.slug}>{department.name}</DepartmentLink>
                  ) : (
                    row.code
                  )
                }
                value={number(Math.round(row[mode]))}
                ratio={row[mode] / maximum}
                color={color}
              />
            );
          })}
        </ol>
      </div>
      <details>
        <summary className="cursor-pointer text-sm underline">
          Tous les départements, outre-mer compris
        </summary>
        <div className="mt-4 max-h-80 overflow-auto">
          <table className="w-full text-left text-sm">
            <thead className="sticky top-0 bg-surface">
              <tr>
                <th scope="col">Département</th>
                <th scope="col">Naissances</th>
                <th scope="col">Pour 10 000</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((row) => {
                const department = DEPARTMENTS_BY_CODE.get(row.code);
                return (
                  <tr key={row.code} className="border-t border-line">
                    <th scope="row" className="py-2">
                      {department ? (
                        <DepartmentLink slug={department.slug}>{department.name}</DepartmentLink>
                      ) : (
                        row.code
                      )}
                    </th>
                    <td>{number(row.count)}</td>
                    <td>{number(Math.round(row.rate))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </details>
      {sorted.length === 0 && (
        <p>
          Aucun effectif départemental publié pour ce prénom sur {NAME_MAP_FROM}–{NAME_LAST_YEAR}.
        </p>
      )}
    </section>
  );
}

export function NamePage({
  data,
  related,
  overallRank,
  year,
}: {
  data: NameStats;
  related: NameSummary[];
  overallRank: number;
  year?: number;
}) {
  const info = profile(data);
  const color = info.gender === "boys" ? BOYS : info.gender === "girls" ? GIRLS : "#1f2544";
  const topDepartment = topDepartments(data)[0];
  const topDepartmentRef = topDepartment ? DEPARTMENTS_BY_CODE.get(topDepartment.code) : undefined;
  return (
    <NamesLayout>
      <Breadcrumbs items={[{ label: "Prénoms", to: "/prenoms" }, { label: data.name }]} />
      <PrenomsNav
        current="/prenoms"
        share={
          <ShareButton
            path={year ? `${namePath.name(data.slug)}?annee=${year}` : namePath.name(data.slug)}
            title={`Le prénom ${data.name} en chiffres`}
            text={`Les stats du prénom ${data.name} sur Listou :`}
            card={(format) => nameCards.name(data.slug, format, year)}
            slug={data.slug}
          />
        }
      />
      <header className="overflow-hidden rounded-[2rem] text-white" style={{ background: color }}>
        <div className="space-y-6 p-8 sm:p-12">
          <p className="text-sm font-bold">
            Prénom {genderWord(info.gender)}
            {info.gender === "mixed"
              ? ""
              : ` · ${ordinal(overallRank)} prénom ${rankedSex(data)}`}{" "}
            depuis {NAME_FIRST_YEAR}
          </p>
          <h1 className="break-words text-6xl sm:text-8xl">{data.name}</h1>
          <p className="max-w-2xl text-lg text-white/90">
            {number(data.total)} naissances recensées en France depuis {NAME_FIRST_YEAR}. Le prénom
            a atteint son sommet en {info.peakYear} avec {number(info.peak)} naissances
            {info.latestRank > 0
              ? `, et se classe ${ordinal(info.latestRank)} en ${NAME_LAST_YEAR}.`
              : info.latestCount > 0
                ? `, et compte ${number(info.latestCount)} naissances en ${NAME_LAST_YEAR}.`
                : `, et n’apparaît plus dans le fichier en ${NAME_LAST_YEAR}.`}
          </p>
          <dl className="grid grid-cols-2 gap-6 lg:grid-cols-4">
            <Fact value={number(data.total)} label="naissances depuis 1900" />
            <Fact
              value={String(info.peakYear)}
              label={`année du pic, ${number(info.peak)} naissances`}
            />
            <Fact value={String(info.median)} label="année médiane de naissance" />
            <Fact
              value={
                info.change === null
                  ? "n.c."
                  : `${info.change > 0 ? "+" : ""}${Math.round(info.change)} %`
              }
              label={`${NAME_LAST_YEAR - 4}–${NAME_LAST_YEAR} contre ${NAME_LAST_YEAR - 9}–${NAME_LAST_YEAR - 5}`}
            />
          </dl>
        </div>
        <Sparkline values={data.years} />
      </header>

      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_19rem] lg:items-start">
        <div className="min-w-0 space-y-10">
          {year ? (
            <Personal data={data} year={year} />
          ) : (
            <section className={`${card} space-y-3`}>
              <h2 className="text-2xl font-bold">Et vous, vous êtes né(e) quand ?</h2>
              <BirthYearForm name={data.name} />
            </section>
          )}

          <Evolution data={data} />
          {/* A single-sex name has little to chart: the split stays a slim strip beside nothing. */}
          <div className={info.gender === "mixed" ? "grid gap-6 lg:grid-cols-2" : "space-y-6"}>
            <Generations data={data} />
            <SexSplit data={data} />
          </div>
          <Geography data={data} />

          <section className={`${card} space-y-4`}>
            <h2 className="text-3xl font-bold">Les repères de {data.name}</h2>
            <ul className="grid gap-3 sm:grid-cols-2">
              {info.best && (
                <li>
                  Meilleur rang : <strong>{ordinal(info.best.rank)}</strong>, en{" "}
                  <YearLink year={info.best.year} className="underline" />.
                </li>
              )}
              <li>
                Année de pic : <YearLink year={info.peakYear} className="underline" /> (
                {number(info.peak)} naissances).
              </li>
              {info.lastRanked && (
                <li>
                  Dernier classement publié : <strong>{ordinal(info.lastRanked.rank)}</strong> en{" "}
                  <YearLink year={info.lastRanked.year} className="underline" />.
                </li>
              )}
              {topDepartment && topDepartmentRef && (
                <li>
                  Département où il est le plus fréquent :{" "}
                  <DepartmentLink slug={topDepartmentRef.slug} className="underline">
                    {topDepartmentRef.name}
                  </DepartmentLink>{" "}
                  ({number(Math.round(topDepartment.rate))} pour 10 000).{" "}
                  <YearDepartmentLink
                    year={NAME_LAST_YEAR}
                    slug={topDepartmentRef.slug}
                    className="underline"
                  >
                    Son top {NAME_LAST_YEAR}
                  </YearDepartmentLink>
                  .
                </li>
              )}
            </ul>
            <p className="text-sm text-muted">
              Le fichier ne permet pas de déduire la signification, l’origine ou la personnalité
              associée à un prénom : il compte des naissances.
            </p>
          </section>

          <section className="space-y-5">
            <h2 className="text-3xl font-bold">Des prénoms de la même époque</h2>
            <p className="text-muted">
              Des prénoms dont la courbe de popularité ressemble à celle de {data.name}.
            </p>
            <ul className="flex flex-wrap gap-3">
              {related.map((name) => (
                <li key={name.slug}>
                  <NameLink
                    slug={name.slug}
                    className="block rounded-full border border-line px-5 py-3 text-lg hover:border-ink"
                  >
                    {name.name}
                  </NameLink>
                </li>
              ))}
            </ul>
          </section>

          <Faq items={faqFor(data, overallRank)} />
        </div>
        <ListouAside />
      </div>

      <section className="rounded-3xl bg-butter/60 p-8 sm:p-12 lg:hidden">
        <h2 className="text-3xl font-bold">Le prénom se précise. La liste de naissance aussi.</h2>
        <p className="mt-4 max-w-xl text-muted">
          Rassemblez vos envies pour l’arrivée de bébé et partagez une seule liste avec vos proches.
        </p>
        <Link to="/" className="btn mt-6">
          Préparer ma liste de naissance
        </Link>
      </section>
      <Source />
    </NamesLayout>
  );
}
